/**
 * 🌌 SOUL PASSIONS - Le Système des Âmes Sœurs
 * 
 * "La loi d'attraction ne ment jamais... Les âmes qui vibrent 
 *  à la même fréquence finissent toujours par se trouver." 💜
 * 
 * Ce système connecte les gens par leurs PASSIONS PROFONDES,
 * pas juste des intérêts superficiels. On cherche les ÂMES JUMELLES !
 * 
 * 🌱 Nature & Terre | 🧘 Éveil & Spiritualité | 📚 Savoir & Réflexion
 * 🎬 Culture & Nostalgie | 🌍 Engagement | 🍹 Sorties & Fêtes
 * 🎨 Créativité | ❤️ Amour & Relations | 🏃 Bien-être & Sport
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Sparkles, Star, Check, X, ChevronRight, 
  Leaf, BookOpen, Film, Globe, Music, Palette,
  Mountain, Sun, Moon, Coffee, Wine, Zap
} from 'lucide-react';
import { Button } from '../../ui/button';

// 🌌 CATÉGORIES DE PASSIONS PROFONDES
export const PASSION_CATEGORIES = [
  {
    id: 'nature',
    name: '🌱 Nature & Terre',
    emoji: '🌱',
    description: 'Pour ceux qui vibrent avec la Terre Mère',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/50',
    icon: Leaf,
  },
  {
    id: 'spirituality',
    name: '🧘 Éveil & Spiritualité',
    emoji: '🧘',
    description: 'Pensée positive, méditation, loi d\'attraction...',
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/50',
    icon: Sun,
  },
  {
    id: 'knowledge',
    name: '📚 Savoir & Réflexion',
    emoji: '📚',
    description: 'Philosophie, lecture, refaire le monde...',
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/50',
    icon: BookOpen,
  },
  {
    id: 'culture',
    name: '🎬 Culture & Nostalgie',
    emoji: '🎬',
    description: 'Vieux films, musique vintage, art...',
    color: 'from-rose-500 to-pink-600',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/50',
    icon: Film,
  },
  {
    id: 'engagement',
    name: '🌍 Engagement & Causes',
    emoji: '🌍',
    description: 'Écologie, humanitaire, changer le monde...',
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/50',
    icon: Globe,
  },
  {
    id: 'nightlife',
    name: '🍹 Sorties & Fêtes',
    emoji: '🍹',
    description: 'Tu sors où ce soir ? Bars, clubs, concerts...',
    color: 'from-fuchsia-500 to-purple-600',
    bgColor: 'bg-fuchsia-500/10',
    borderColor: 'border-fuchsia-500/50',
    icon: Wine,
  },
  {
    id: 'creativity',
    name: '🎨 Créativité & Art',
    emoji: '🎨',
    description: 'Musique, peinture, écriture, photo...',
    color: 'from-indigo-500 to-blue-600',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/50',
    icon: Palette,
  },
  {
    id: 'adventure',
    name: '🏔️ Aventure & Voyage',
    emoji: '🏔️',
    description: 'Montagne, road trips, découvertes...',
    color: 'from-teal-500 to-cyan-600',
    bgColor: 'bg-teal-500/10',
    borderColor: 'border-teal-500/50',
    icon: Mountain,
  },
  {
    id: 'wellness',
    name: '🏃 Bien-être & Sport',
    emoji: '🏃',
    description: 'Yoga, running, gym, vie saine...',
    color: 'from-lime-500 to-green-600',
    bgColor: 'bg-lime-500/10',
    borderColor: 'border-lime-500/50',
    icon: Zap,
  },
];

// 🌟 PASSIONS DÉTAILLÉES PAR CATÉGORIE
export const PASSIONS = {
  nature: [
    { id: 'permaculture', name: '🌱 Permaculture', description: 'Cultiver en harmonie avec la nature' },
    { id: 'gardening', name: '🌻 Jardinage', description: 'Faire pousser des merveilles' },
    { id: 'hiking', name: '🥾 Randonnée', description: 'Se perdre pour mieux se trouver' },
    { id: 'mountains', name: '🏔️ Montagne', description: 'Plus près des étoiles' },
    { id: 'birds', name: '🐦 Observation des oiseaux', description: 'La poésie du ciel' },
    { id: 'forests', name: '🌲 Forêts & Bains de forêt', description: 'Se ressourcer dans les arbres' },
    { id: 'ocean', name: '🌊 Océan & Mer', description: 'L\'appel du large' },
    { id: 'animals', name: '🦋 Animaux & Biodiversité', description: 'Protéger le vivant' },
    { id: 'stars', name: '⭐ Astronomie', description: 'Contempler l\'infini' },
    { id: 'camping', name: '⛺ Camping sauvage', description: 'Dormir sous les étoiles' },
  ],
  spirituality: [
    { id: 'positive_thinking', name: '✨ Pensée positive', description: 'L\'énergie que tu émets, tu l\'attires' },
    { id: 'meditation', name: '🧘 Méditation', description: 'Le silence qui parle' },
    { id: 'law_of_attraction', name: '🌌 Loi d\'attraction', description: 'Manifester ses rêves' },
    { id: 'personal_dev', name: '📈 Développement personnel', description: 'Devenir sa meilleure version' },
    { id: 'yoga', name: '🕉️ Yoga', description: 'Corps, âme et esprit' },
    { id: 'tarot', name: '🔮 Tarot & Oracles', description: 'Écouter l\'univers' },
    { id: 'energy_healing', name: '💫 Énergies & Soins', description: 'Guérir par les vibrations' },
    { id: 'dreams', name: '💭 Rêves lucides', description: 'Explorer l\'inconscient' },
    { id: 'gratitude', name: '🙏 Gratitude', description: 'Remercier chaque instant' },
    { id: 'mindfulness', name: '🌸 Pleine conscience', description: 'Vivre l\'instant présent' },
  ],
  knowledge: [
    { id: 'reading', name: '📖 Lecture passionnée', description: 'Dévorer des livres' },
    { id: 'philosophy', name: '🤔 Philosophie', description: 'Questionner l\'existence' },
    { id: 'deep_talks', name: '💬 Discussions profondes', description: 'Refaire le monde jusqu\'à 4h du mat\'' },
    { id: 'history', name: '🏛️ Histoire', description: 'Comprendre d\'où on vient' },
    { id: 'science', name: '🔬 Sciences', description: 'Percer les mystères' },
    { id: 'psychology', name: '🧠 Psychologie', description: 'Comprendre l\'humain' },
    { id: 'languages', name: '🗣️ Langues', description: 'Ouvrir des portes' },
    { id: 'podcasts', name: '🎙️ Podcasts', description: 'Apprendre en marchant' },
    { id: 'documentaries', name: '🎥 Documentaires', description: 'S\'éveiller au monde' },
    { id: 'debates', name: '⚖️ Débats passionnés', description: 'Confronter les idées' },
  ],
  culture: [
    { id: 'old_movies', name: '🎬 Vieux films', description: 'La magie du cinéma d\'antan' },
    { id: 'classic_music', name: '🎵 Musique classique', description: 'Mozart, Beethoven, Chopin...' },
    { id: 'vintage', name: '📻 Vintage & Rétro', description: 'Le charme de l\'ancien' },
    { id: 'theater', name: '🎭 Théâtre', description: 'L\'émotion en direct' },
    { id: 'museums', name: '🖼️ Musées & Expos', description: 'Se cultiver en beauté' },
    { id: 'literature', name: '✍️ Littérature classique', description: 'Les grands auteurs' },
    { id: 'jazz', name: '🎷 Jazz & Blues', description: 'L\'âme dans la musique' },
    { id: 'opera', name: '🎤 Opéra', description: 'La voix qui transcende' },
    { id: 'vinyl', name: '💿 Vinyles & Collectors', description: 'Le son authentique' },
    { id: 'poetry', name: '📝 Poésie', description: 'Les mots qui touchent l\'âme' },
  ],
  engagement: [
    { id: 'ecology', name: '♻️ Écologie', description: 'Protéger notre planète' },
    { id: 'humanitarian', name: '🤝 Humanitaire', description: 'Aider ceux qui en ont besoin' },
    { id: 'animal_rights', name: '🐾 Droits des animaux', description: 'Défendre les sans-voix' },
    { id: 'social_justice', name: '⚖️ Justice sociale', description: 'Un monde plus juste' },
    { id: 'volunteering', name: '💪 Bénévolat', description: 'Donner de son temps' },
    { id: 'zero_waste', name: '🌿 Zéro déchet', description: 'Moins c\'est mieux' },
    { id: 'vegan', name: '🥗 Véganisme', description: 'Respecter le vivant' },
    { id: 'local', name: '🏪 Consommer local', description: 'Soutenir les producteurs' },
    { id: 'education', name: '📚 Éducation pour tous', description: 'Le savoir comme droit' },
    { id: 'feminism', name: '♀️ Féminisme', description: 'L\'égalité pour tous' },
  ],
  nightlife: [
    { id: 'bars', name: '🍺 Bars sympas', description: 'Bonne ambiance garantie' },
    { id: 'clubs', name: '🪩 Boîtes de nuit', description: 'Danser jusqu\'au bout de la nuit' },
    { id: 'concerts', name: '🎸 Concerts live', description: 'L\'énergie du live' },
    { id: 'festivals', name: '🎪 Festivals', description: 'Vivre des moments fous' },
    { id: 'karaoke', name: '🎤 Karaoké', description: 'Chanter à tue-tête' },
    { id: 'cocktails', name: '🍹 Cocktails', description: 'L\'art de la mixologie' },
    { id: 'apero', name: '🥂 Apéros entre potes', description: 'Le bonheur simple' },
    { id: 'dancing', name: '💃 Danser', description: 'Lâcher prise sur le dancefloor' },
    { id: 'late_nights', name: '🌙 Nuits blanches', description: 'Dormir c\'est pour les faibles' },
    { id: 'rooftops', name: '🏙️ Rooftops', description: 'Vue sur la ville' },
  ],
  creativity: [
    { id: 'music', name: '🎵 Musique', description: 'Jouer ou composer' },
    { id: 'painting', name: '🎨 Peinture', description: 'Exprimer par les couleurs' },
    { id: 'writing', name: '✍️ Écriture', description: 'Raconter des histoires' },
    { id: 'photography', name: '📷 Photographie', description: 'Capturer l\'instant' },
    { id: 'diy', name: '🛠️ DIY & Bricolage', description: 'Créer de ses mains' },
    { id: 'cooking', name: '👨‍🍳 Cuisine créative', description: 'L\'art culinaire' },
    { id: 'dance', name: '💃 Danse', description: 'S\'exprimer par le corps' },
    { id: 'design', name: '🖌️ Design', description: 'Créer du beau' },
    { id: 'crafts', name: '🧶 Artisanat', description: 'Fait main avec amour' },
    { id: 'video', name: '🎬 Vidéo & Création', description: 'Raconter en images' },
  ],
  adventure: [
    { id: 'travel', name: '✈️ Voyages', description: 'Découvrir le monde' },
    { id: 'road_trips', name: '🚗 Road trips', description: 'La route est le but' },
    { id: 'backpacking', name: '🎒 Backpacking', description: 'Voyager léger' },
    { id: 'extreme_sports', name: '🪂 Sports extrêmes', description: 'L\'adrénaline pure' },
    { id: 'surfing', name: '🏄 Surf', description: 'Chevaucher les vagues' },
    { id: 'climbing', name: '🧗 Escalade', description: 'Grimper toujours plus haut' },
    { id: 'diving', name: '🤿 Plongée', description: 'Explorer les profondeurs' },
    { id: 'skiing', name: '⛷️ Ski & Snow', description: 'Glisser sur la neige' },
    { id: 'cycling', name: '🚴 Vélo', description: 'La liberté à deux roues' },
    { id: 'vanlife', name: '🚐 Van life', description: 'La maison sur roues' },
  ],
  wellness: [
    { id: 'fitness', name: '💪 Fitness', description: 'Se dépasser' },
    { id: 'running', name: '🏃 Running', description: 'Courir vers soi-même' },
    { id: 'healthy_eating', name: '🥗 Alimentation saine', description: 'Tu es ce que tu manges' },
    { id: 'sleep', name: '😴 Sommeil de qualité', description: 'Bien dormir = bien vivre' },
    { id: 'spa', name: '🧖 Spa & Détente', description: 'Prendre soin de soi' },
    { id: 'martial_arts', name: '🥋 Arts martiaux', description: 'Corps et esprit' },
    { id: 'swimming', name: '🏊 Natation', description: 'L\'eau qui libère' },
    { id: 'mental_health', name: '🧠 Santé mentale', description: 'Prendre soin de son esprit' },
    { id: 'breathing', name: '🌬️ Respiration', description: 'Le souffle de vie' },
    { id: 'stretching', name: '🤸 Étirements', description: 'Flexibilité du corps et de l\'esprit' },
  ],
};

// 🔮 QUESTIONS DE L'ÂME - Pour mieux matcher
export const SOUL_QUESTIONS = [
  {
    id: 'ideal_evening',
    question: '🌙 Ta soirée parfaite ?',
    options: [
      { id: 'nature_walk', text: '🌲 Balade en forêt au coucher du soleil', categories: ['nature', 'spirituality'] },
      { id: 'deep_talk', text: '💬 Discussion passionnée jusqu\'à 4h du mat\'', categories: ['knowledge'] },
      { id: 'party', text: '🍹 Bar sympa puis dancefloor !', categories: ['nightlife'] },
      { id: 'movie_couch', text: '🎬 Vieux film + plaid + thé', categories: ['culture'] },
      { id: 'create', text: '🎨 Créer quelque chose de mes mains', categories: ['creativity'] },
    ]
  },
  {
    id: 'recharge',
    question: '⚡ Comment tu recharges tes batteries ?',
    options: [
      { id: 'alone_nature', text: '🌱 Seul(e) dans la nature', categories: ['nature', 'spirituality'] },
      { id: 'with_friends', text: '👯 Entouré(e) de mes potes', categories: ['nightlife'] },
      { id: 'reading', text: '📚 Plongé(e) dans un bon livre', categories: ['knowledge', 'culture'] },
      { id: 'sport', text: '🏃 En me défoulant au sport', categories: ['wellness', 'adventure'] },
      { id: 'creating', text: '🎨 En créant quelque chose', categories: ['creativity'] },
    ]
  },
  {
    id: 'dream_vacation',
    question: '✈️ Tes vacances de rêve ?',
    options: [
      { id: 'mountains', text: '🏔️ Randonnée en montagne', categories: ['nature', 'adventure'] },
      { id: 'spiritual_retreat', text: '🧘 Retraite spirituelle', categories: ['spirituality'] },
      { id: 'city_culture', text: '🏛️ Capitale culturelle européenne', categories: ['culture', 'knowledge'] },
      { id: 'beach_party', text: '🏖️ Plage + soirées', categories: ['nightlife', 'adventure'] },
      { id: 'road_trip', text: '🚐 Road trip sans plan', categories: ['adventure'] },
    ]
  },
  {
    id: 'life_motto',
    question: '💭 Ta philosophie de vie ?',
    options: [
      { id: 'carpe_diem', text: '☀️ Carpe Diem - Vis l\'instant', categories: ['nightlife', 'adventure'] },
      { id: 'universe', text: '🌌 L\'univers conspire pour toi', categories: ['spirituality'] },
      { id: 'knowledge_power', text: '📚 Le savoir est pouvoir', categories: ['knowledge'] },
      { id: 'nature_heals', text: '🌿 La nature guérit tout', categories: ['nature'] },
      { id: 'create_life', text: '🎨 Crée la vie que tu veux', categories: ['creativity', 'spirituality'] },
    ]
  },
  {
    id: 'weekend_plan',
    question: '📅 Ce weekend idéal ?',
    options: [
      { id: 'garden', text: '🌱 Jardiner et prendre soin de mes plantes', categories: ['nature'] },
      { id: 'brunch_friends', text: '🥂 Brunch puis balade avec les potes', categories: ['nightlife'] },
      { id: 'museum_cafe', text: '🖼️ Expo puis café littéraire', categories: ['culture', 'knowledge'] },
      { id: 'yoga_meditation', text: '🧘 Yoga, méditation, cocooning', categories: ['spirituality', 'wellness'] },
      { id: 'adventure', text: '🏔️ Partir à l\'aventure !', categories: ['adventure'] },
    ]
  },
];

// 🌟 Calcul du score de compatibilité d'âmes
export const calculateSoulCompatibility = (user1Passions, user2Passions, user1Answers, user2Answers) => {
  let score = 0;
  let maxScore = 0;
  let commonPassions = [];
  let commonCategories = new Set();
  
  // 1. Passions communes (40% du score)
  const passionWeight = 40;
  const allCategories = Object.keys(PASSIONS);
  
  allCategories.forEach(category => {
    const user1Cat = user1Passions[category] || [];
    const user2Cat = user2Passions[category] || [];
    
    user1Cat.forEach(passion => {
      maxScore += passionWeight / 10;
      if (user2Cat.includes(passion)) {
        score += passionWeight / 10;
        commonPassions.push(passion);
        commonCategories.add(category);
      }
    });
  });
  
  // 2. Questions de l'âme communes (40% du score)
  const questionWeight = 40;
  if (user1Answers && user2Answers) {
    SOUL_QUESTIONS.forEach(q => {
      maxScore += questionWeight / SOUL_QUESTIONS.length;
      if (user1Answers[q.id] === user2Answers[q.id]) {
        score += questionWeight / SOUL_QUESTIONS.length;
      }
    });
  }
  
  // 3. Catégories principales communes (20% du score)
  const categoryWeight = 20;
  const user1TopCategories = getTopCategories(user1Passions);
  const user2TopCategories = getTopCategories(user2Passions);
  
  user1TopCategories.forEach(cat => {
    maxScore += categoryWeight / 3;
    if (user2TopCategories.includes(cat)) {
      score += categoryWeight / 3;
    }
  });
  
  // Calcul du pourcentage
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  
  // Niveau de compatibilité
  let level = 'compatible';
  let message = '';
  let emoji = '💜';
  
  if (percentage >= 85) {
    level = 'twin_flame';
    message = 'Âmes jumelles ! 🔥 Connexion cosmique !';
    emoji = '🔥';
  } else if (percentage >= 70) {
    level = 'soulmate';
    message = 'Âmes sœurs ! ✨ Vous vibrez ensemble !';
    emoji = '✨';
  } else if (percentage >= 55) {
    level = 'high';
    message = 'Forte connexion ! 💜 À explorer...';
    emoji = '💜';
  } else if (percentage >= 40) {
    level = 'medium';
    message = 'Des points communs ! 🌟';
    emoji = '🌟';
  } else {
    level = 'low';
    message = 'Opposés qui s\'attirent ? 🎭';
    emoji = '🎭';
  }
  
  return {
    percentage,
    level,
    message,
    emoji,
    commonPassions,
    commonCategories: Array.from(commonCategories),
  };
};

// Helper: Obtenir les 3 catégories principales
const getTopCategories = (passions) => {
  const counts = {};
  Object.entries(passions).forEach(([category, items]) => {
    counts[category] = (items || []).length;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([cat]) => cat);
};

// 🎨 Composant de sélection des passions
export const PassionSelector = ({ 
  selectedPassions = {}, 
  onPassionsChange, 
  isDarkMode = true,
  maxPerCategory = 5,
}) => {
  const [activeCategory, setActiveCategory] = useState('nature');
  
  const handleTogglePassion = (category, passionId) => {
    const current = selectedPassions[category] || [];
    let updated;
    
    if (current.includes(passionId)) {
      updated = current.filter(p => p !== passionId);
    } else if (current.length < maxPerCategory) {
      updated = [...current, passionId];
    } else {
      return; // Max atteint
    }
    
    // Sécurité: vérifier que onPassionsChange est une fonction
    if (typeof onPassionsChange === 'function') {
      onPassionsChange({
        ...selectedPassions,
        [category]: updated,
      });
    }
  };
  
  const getTotalSelected = () => {
    return Object.values(selectedPassions).reduce((acc, arr) => acc + (arr?.length || 0), 0);
  };
  
  const categoryData = PASSION_CATEGORIES.find(c => c.id === activeCategory);
  const categoryPassions = PASSIONS[activeCategory] || [];
  const selectedInCategory = selectedPassions[activeCategory] || [];
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          🌌 Tes Passions Profondes
        </h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Choisis ce qui fait vibrer ton âme... L'univers fera le reste ✨
        </p>
        <div className={`mt-2 text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
          {getTotalSelected()} passions sélectionnées
        </div>
      </div>
      
      {/* Catégories */}
      <div className="flex flex-wrap gap-2 justify-center">
        {PASSION_CATEGORIES.map((cat) => {
          const count = (selectedPassions[cat.id] || []).length;
          const isActive = activeCategory === cat.id;
          
          return (
            <motion.button
              key={cat.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(cat.id)}
              className={`relative px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                isActive
                  ? `bg-gradient-to-r ${cat.color} text-white shadow-lg`
                  : isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.emoji} {cat.name.split(' ').slice(1).join(' ')}
              {count > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-pink-500 text-white text-xs flex items-center justify-center">
                  {count}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>
      
      {/* Description catégorie */}
      <div className={`text-center p-3 rounded-xl ${categoryData?.bgColor} border ${categoryData?.borderColor}`}>
        <p className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          {categoryData?.description}
        </p>
      </div>
      
      {/* Grille des passions */}
      <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
        {categoryPassions.map((passion) => {
          const isSelected = selectedInCategory.includes(passion.id);
          
          return (
            <motion.button
              key={passion.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleTogglePassion(activeCategory, passion.id)}
              className={`p-3 rounded-xl text-left transition-all ${
                isSelected
                  ? `bg-gradient-to-r ${categoryData?.color} text-white shadow-lg`
                  : isDarkMode
                    ? 'bg-gray-800 hover:bg-gray-700'
                    : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className={`font-medium text-sm ${isSelected ? 'text-white' : isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {passion.name}
                  </p>
                  <p className={`text-xs mt-0.5 ${isSelected ? 'text-white/80' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {passion.description}
                  </p>
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 text-white flex-shrink-0" />
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
      
      {/* Limite */}
      <p className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Max {maxPerCategory} par catégorie • Choisis ce qui te représente vraiment 💜
      </p>
    </div>
  );
};

// 🔮 Composant Questions de l'Âme
export const SoulQuestionsWizard = ({ 
  answers = {}, 
  onAnswersChange, 
  isDarkMode = true,
  onComplete,
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  
  const question = SOUL_QUESTIONS[currentQuestion];
  const isLast = currentQuestion === SOUL_QUESTIONS.length - 1;
  const progress = ((currentQuestion + 1) / SOUL_QUESTIONS.length) * 100;
  
  const handleAnswer = (optionId) => {
    const newAnswers = { ...answers, [question.id]: optionId };
    // Sécurité: vérifier que onAnswersChange est une fonction
    if (typeof onAnswersChange === 'function') {
      onAnswersChange(newAnswers);
    }
    
    if (isLast) {
      onComplete?.(newAnswers);
    } else {
      setTimeout(() => setCurrentQuestion(prev => prev + 1), 300);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
            Question {currentQuestion + 1}/{SOUL_QUESTIONS.length}
          </span>
          <span className={isDarkMode ? 'text-purple-400' : 'text-purple-600'}>
            {Math.round(progress)}%
          </span>
        </div>
        <div className={`h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
          />
        </div>
      </div>
      
      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={question.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-4"
        >
          <h3 className={`text-xl font-bold text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {question.question}
          </h3>
          
          <div className="space-y-2">
            {question.options.map((option) => {
              const isSelected = answers[question.id] === option.id;
              
              return (
                <motion.button
                  key={option.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAnswer(option.id)}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                      : isDarkMode
                        ? 'bg-gray-800 hover:bg-gray-700 text-gray-200'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{option.text}</span>
                    {isSelected && <Check className="w-5 h-5" />}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>
      
      {/* Navigation */}
      {currentQuestion > 0 && (
        <Button
          variant="ghost"
          onClick={() => setCurrentQuestion(prev => prev - 1)}
          className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}
        >
          ← Question précédente
        </Button>
      )}
    </div>
  );
};

// 💜 Affichage de la compatibilité
export const SoulCompatibilityBadge = ({ compatibility, size = 'md', showDetails = false }) => {
  if (!compatibility) return null;
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2',
  };
  
  const levelColors = {
    twin_flame: 'from-orange-500 to-red-500',
    soulmate: 'from-purple-500 to-pink-500',
    high: 'from-violet-500 to-purple-500',
    medium: 'from-blue-500 to-cyan-500',
    low: 'from-gray-500 to-gray-600',
  };
  
  return (
    <div className="flex flex-col items-center gap-1">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className={`rounded-full bg-gradient-to-r ${levelColors[compatibility.level]} text-white font-bold ${sizeClasses[size]}`}
      >
        {compatibility.emoji} {compatibility.percentage}%
      </motion.div>
      
      {showDetails && (
        <p className="text-xs text-gray-400 text-center">
          {compatibility.message}
        </p>
      )}
    </div>
  );
};

export default PassionSelector;
